import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Button, Alert, ConfigProvider } from 'antd';
import type { AppDispatch, RootState } from '../../redux/store';
import {
  fetchUserProfile,
  updateUserProfile,
  setEditingSection,
  setSettingsSaved,
} from '../../redux/Settings';
import type { UpdateProfilePayload } from '../../redux/Settings';
import {
  Container,
  HeaderSection,
  Title,
  Subtitle,
  GridContainer,
} from './Styles';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { notification } from '../../commonComponents/Notification/Notification';
import ProfileSection from './ProfileSection';
import ProfileEditForm from './ProfileEditForm';
import PersonalInformationSection from './PersonalInformationSection';
import AddressSection from './AddressSection';
import TaxConfigurationSection from './TaxConfigurationSection';
import PreferencesSection from './PreferencesSection';
import TwoStepVerificationSection from './TwoStepVerificationSection';
import type { SettingsProps, TwoFactorMethod } from './types';

const Settings: React.FC<SettingsProps> = ({ role }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const theme = useMemo<{ mode: 'light' | 'dark' }>(
    () => ({ mode: isDarkMode ? 'dark' : 'light' }),
    [isDarkMode]
  );

  // Redux state
  const {
    user,
    loading,
    error,
    editingSection,
    settingsSaved,
  } = useSelector((state: RootState) => state.settings);

  // Local state
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    address: '',
    email: '',
    phone: '',
    publicPhone: '',
    tax: 0,
    loyaltyEnabled: false,
    loyaltyRedeemRatio: '',
    loyaltySpendRatio: '',
    receiveNotifications: false,
    autoRenewSubscription: false,
    twoFactorEnabled: false,
    twoFactorMethod: 'phone' as TwoFactorMethod,
    twoFactorPhone: '',
    twoFactorEmail: '',
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);


  // Fetch user profile on mount
  useEffect(() => {
    dispatch(fetchUserProfile({ role }));
  }, [dispatch, role]);

  // Update form data when user profile is loaded
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        address: user.address || '',
        email: user.email || '',
        phone: user.phone || '',
        publicPhone: user.publicPhone || '',
        loyaltyEnabled: user.loyaltyEnabled || false,
        loyaltyRedeemRatio: user.loyaltyRedeemRatio || '',
        loyaltySpendRatio: user.loyaltySpendRatio || '',
        tax:
          user.tax !== undefined && user.tax !== null && !isNaN(user.tax)
            ? user.tax * 100
            : 0,
        receiveNotifications: user.receiveNotifications || false,
        autoRenewSubscription: user.autoRenewSubscription || false,
        twoFactorEnabled: user.twoFactorEnabled || false,
        twoFactorMethod: (user.twoFactorMethod || 'phone') as TwoFactorMethod,
        twoFactorPhone: user.twoFactorPhone || '',
        twoFactorEmail: user.twoFactorEmail || '',
      });
    }
  }, [user]);


  // Handlers
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    },
    []
  );

  const handleNumberChange = useCallback((name: string, value: number | null) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value || 0,
    }));
  }, []);

  const handleSwitchChange = useCallback((field: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: checked,
    }));
  }, []);

  const handleTwoFactorMethodChange = useCallback((method: TwoFactorMethod) => {
    setFormData((prev) => ({
      ...prev,
      twoFactorMethod: method,
    }));
  }, []);

  const handleEdit = useCallback(
    (section: string) => {
      dispatch(setEditingSection(section));
      dispatch(setSettingsSaved(false));
    },
    [dispatch]
  );

  const handleSave = useCallback(async () => {
    if (!formData.name?.trim() || !formData.username?.trim() || !formData.email?.trim()) {
      notification.error('Name, username, and email are required fields and cannot be empty.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      notification.error('Please enter a valid email address.');
      return;
    }

    if (!user) return;

    try {
      if (formData.twoFactorEnabled) {
        if (formData.twoFactorMethod === 'phone' && !formData.twoFactorPhone?.trim()) {
          notification.error('Please enter your personal phone number for two-step verification.');
          return;
        }
        if (formData.twoFactorMethod === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!formData.twoFactorEmail?.trim() || !emailRegex.test(formData.twoFactorEmail.trim())) {
            notification.error('Please enter a valid personal email for two-step verification.');
            return;
          }
        }
      }

      const parseLoyaltyRatio = (value: string): number | undefined => {
        if (value === undefined || value === null || String(value).trim() === '') return undefined;
        const num = Number(String(value).trim());
        return Number.isFinite(num) ? num : undefined;
      };

      const loyaltyRedeemNum = parseLoyaltyRatio(formData.loyaltyRedeemRatio);
      const loyaltySpendNum = parseLoyaltyRatio(formData.loyaltySpendRatio);

      const updatedData: UpdateProfilePayload = {
        name: formData.name.trim(),
        username: formData.username.trim(),
        address: formData.address.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        publicPhone: formData.publicPhone?.trim() || undefined,
        loyaltyEnabled: formData.loyaltyEnabled,
        tax: formData.tax / 100,
        receiveNotifications: formData.receiveNotifications,
        autoRenewSubscription: formData.autoRenewSubscription,
        twoFactorEnabled: formData.twoFactorEnabled,
        twoFactorMethod: formData.twoFactorMethod,
        twoFactorPhone: formData.twoFactorPhone?.trim() || undefined,
        twoFactorEmail: formData.twoFactorEmail?.trim() || undefined,
        ...(loyaltyRedeemNum !== undefined && { loyaltyRedeemRatio: loyaltyRedeemNum }),
        ...(loyaltySpendNum !== undefined && { loyaltySpendRatio: loyaltySpendNum }),
      };

      await dispatch(
        updateUserProfile({
          role,
          userId: user.id,
          data: updatedData,
          logoFile: logoFile || undefined,
          removeLogo: removeLogo,
        })
      ).unwrap();

      // Clear logo file and remove flag after successful upload
      setLogoFile(null);
      setRemoveLogo(false);

      notification.success('Settings updated successfully!');
    } catch (error: any) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to update settings.';
      notification.error(message);
    }
  }, [dispatch, formData, role, user, logoFile, removeLogo]);

  const handleCancel = useCallback(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        address: user.address || '',
        email: user.email || '',
        phone: user.phone || '',
        publicPhone: user.publicPhone || '',
        loyaltyEnabled: user.loyaltyEnabled || false,
        loyaltyRedeemRatio: user.loyaltyRedeemRatio || '',
        loyaltySpendRatio: user.loyaltySpendRatio || '',
        tax:
          user.tax !== undefined && user.tax !== null && !isNaN(user.tax)
            ? user.tax * 100
            : 0,
        receiveNotifications: user.receiveNotifications || false,
        autoRenewSubscription: user.autoRenewSubscription || false,
        twoFactorEnabled: user.twoFactorEnabled || false,
        twoFactorMethod: (user.twoFactorMethod || 'phone') as TwoFactorMethod,
        twoFactorPhone: user.twoFactorPhone || '',
        twoFactorEmail: user.twoFactorEmail || '',
      });
    }
    setLogoFile(null);
    setRemoveLogo(false);
    dispatch(setEditingSection(null));
  }, [dispatch, user]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchUserProfile({ role }));
  }, [dispatch, role]);


  if (error && !user && !loading.profile) {
    return (
      <ThemeProvider theme={theme}>
        <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
          <Container>
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              action={
                <Button size="small" onClick={handleRefresh}>
                  Try Again
                </Button>
              }
            />
          </Container>
        </ConfigProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <div>
              <Title level={1}>Account Settings</Title>
              <Subtitle>Manage your profile and preferences</Subtitle>
            </div>
          </HeaderSection>

          <ProfileSection
            user={user}
            role={role}
            editingSection={editingSection}
            loading={loading.update}
            profileLoading={loading.profile}
            onEdit={() => handleEdit('profile')}
          />

          {editingSection === 'profile' && user && (
            <ProfileEditForm
              formData={{
                name: formData.name,
                username: formData.username,
                address: formData.address,
              }}
              logo={(user as any).logo || (user as any).merchant?.logo || null}
              loading={loading.update}
              onInputChange={handleInputChange}
              onLogoChange={(file) => {
                setLogoFile(file);
                setRemoveLogo(file === null);
              }}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          )}

          <GridContainer>
            <PersonalInformationSection
              user={user}
              editingSection={editingSection}
              formData={{
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
              }}
              loading={loading.update}
              profileLoading={loading.profile}
              onEdit={() => handleEdit('personal')}
              onInputChange={handleInputChange}
              onSave={handleSave}
              onCancel={handleCancel}
            />

            <AddressSection
              user={user}
              editingSection={editingSection}
              formData={{ 
                address: formData.address,
                publicPhone: formData.publicPhone,
              }}
              loading={loading.update}
              profileLoading={loading.profile}
              onEdit={() => handleEdit('address')}
              onInputChange={handleInputChange}
              onSave={handleSave}
              onCancel={handleCancel}
            />

            <TaxConfigurationSection
              user={user}
              editingSection={editingSection}
              formData={{ tax: formData.tax }}
              loading={loading.update}
              profileLoading={loading.profile}
              onEdit={() => handleEdit('tax')}
              onNumberChange={handleNumberChange}
              onSave={handleSave}
              onCancel={handleCancel}
            />

            <PreferencesSection
              editingSection={editingSection}
              formData={{
                receiveNotifications: formData.receiveNotifications,
                autoRenewSubscription: formData.autoRenewSubscription,
              }}
              loading={loading.update}
              profileLoading={loading.profile}
              onEdit={() => handleEdit('additional')}
              onSwitchChange={handleSwitchChange}
              onSave={handleSave}
              onCancel={handleCancel}
            />

            <TwoStepVerificationSection
              editingSection={editingSection}
              formData={{
                twoFactorEnabled: formData.twoFactorEnabled,
                twoFactorMethod: formData.twoFactorMethod,
                twoFactorPhone: formData.twoFactorPhone,
                twoFactorEmail: formData.twoFactorEmail,
              }}
              loading={loading.update}
              profileLoading={loading.profile}
              onEdit={() => handleEdit('twoStep')}
              onSwitchChange={handleSwitchChange}
              onInputChange={handleInputChange}
              onMethodChange={handleTwoFactorMethodChange}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </GridContainer>

          {settingsSaved && !editingSection && (
            <Alert
              message="Settings Saved!"
              description="Your account settings have been updated successfully."
              type="success"
              showIcon
              closable
              style={{ marginTop: 24 }}
            />
          )}
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Settings);
